import type { UseFormMethods } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { optionalPhoneRegex, phoneRegex, specialAlphaNumericRegex } from "@utils/formControls";
import { ICustomerDetailsForm } from "@features/customerDetails/ICustomerDetailsForm";
import { keycloakUtils } from "itsf-ui-common";
import { FormGroup, Grid } from "@mui/material";
import { LabeledDateTextField } from "@common/LabeledDateTextField/LabeledDateTextField";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";
import { CustomerDetailsEmailFields } from "@features/customerDetails/CustomerDetailsEmailFields";

interface IParams {
    formMethods: UseFormMethods<ICustomerDetailsForm>;
    hasBroadbandInCart: boolean;
    emailError?: string;
}

export const CustomerDetailsPersonalDetails = (props: IParams) => {
    const isAuthenticated = keycloakUtils.isAuthenticated();
    const { t } = useTranslation(["customerDetails", "validation"]);
    const { formMethods, hasBroadbandInCart = false, emailError } = props;

    const mobileNumberValidation = {
        required: hasBroadbandInCart ? t("validation:required") : false,
        validate: hasBroadbandInCart ? phoneRegex : optionalPhoneRegex,
    };

    return (
        <section>
            <FormGroup>
                <Grid columnSpacing={4} container rowSpacing={2}>
                    <CustomerDetailsEmailFields emailError={emailError} formMethods={formMethods} />
                    <Grid item md={6} xs={12}>
                        <LabeledTextField
                            disabled={isAuthenticated}
                            error={Boolean(formMethods.errors.firstName)}
                            fullWidth
                            helperText={formMethods.errors.firstName?.message}
                            id="firstName"
                            inputRef={formMethods.register({
                                required: t("validation:required"),
                                validate: specialAlphaNumericRegex,
                            })}
                            isReadOnly={isAuthenticated}
                            isRequired
                            labelText={t("customerDetails:firstName")}
                            name="firstName"
                            placeholder="John"
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <LabeledTextField
                            disabled={isAuthenticated}
                            error={Boolean(formMethods.errors.lastName)}
                            fullWidth
                            helperText={formMethods.errors.lastName?.message}
                            id="lastName"
                            inputRef={formMethods.register({
                                required: t("validation:required"),
                                validate: specialAlphaNumericRegex,
                            })}
                            isReadOnly={isAuthenticated}
                            isRequired
                            labelText={t("customerDetails:lastName")}
                            name="lastName"
                            placeholder="Doe"
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <LabeledTextField
                            error={Boolean(formMethods.errors.mobileNumber)}
                            fullWidth
                            helperText={
                                formMethods.errors.mobileNumber?.message ?? (
                                    <Trans i18nKey="contactNumberHelperText" ns="customerDetails" />
                                )
                            }
                            inputRef={formMethods.register(mobileNumberValidation)}
                            isRequired={hasBroadbandInCart}
                            labelText={t("customerDetails:contactNumber")}
                            name="mobileNumber"
                            placeholder="0851234567"
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <LabeledDateTextField
                            disabled={isAuthenticated}
                            formMethods={formMethods}
                            fullWidth
                            helperText={t("customerDetails:birthDateHelperText")}
                            id="birthDate"
                            isReadOnly={isAuthenticated}
                            isRequired
                            labelText={t("customerDetails:dateBirth")}
                            name="birthDate"
                        />
                    </Grid>
                </Grid>
            </FormGroup>
        </section>
    );
};
